/**
 * Challenge Card — horizontal layout: cover left + details right.
 * Used in both Shaxsiy tab and Ommaviy browse tabs.
 */

import { useState, useCallback, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Users, Calendar, Clock, Lock, Trophy,
  MoreVertical, Share2, LogOut, Copy, ExternalLink,
  ChevronRight,
} from 'lucide-react'
import { differenceInDays } from 'date-fns'
import { triggerHapticImpact as hapticImpact } from '@turnifly/shared/utils'
import { getChallengeShareLink } from '@turnifly/shared/api'

interface ChallengeCardProps {
  challengeId: string
  name: string
  coverEmoji: string | null
  coverGradient: string | null
  coverImageUrl: string | null
  type: string
  status: string
  startDate?: string | null
  endDate?: string | null
  durationDays: number
  membersCount: number
  attemptNumber?: number
  completionRate?: number | null
  memberStatus?: string
  shortCode?: string | null
  isCreator?: boolean
  creatorName?: string | null
  timeModel?: string
  onLeave?: (challengeId: string) => void
  showJoinBtn?: boolean
}

export const ChallengeCard = memo(function ChallengeCard({
  challengeId, name, coverEmoji, coverGradient, coverImageUrl,
  type, status, startDate, endDate, durationDays, membersCount,
  attemptNumber, completionRate, memberStatus, shortCode,
  isCreator, creatorName, timeModel, onLeave, showJoinBtn,
}: ChallengeCardProps): React.ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const bgStyle = coverImageUrl
    ? { backgroundImage: `url(${coverImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : coverGradient
      ? { background: coverGradient }
      : { background: 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(34,197,94,0.3))' }

  const daysLeft = endDate ? Math.max(0, differenceInDays(new Date(endDate), new Date())) : null
  const daysUntilStart = startDate ? differenceInDays(new Date(startDate), new Date()) : null
  const isUpcoming = status === 'upcoming' && daysUntilStart != null && daysUntilStart > 0
  const isActive = status === 'active'
  const isCompleted = status === 'completed'
  const isPrivate = type === 'private'
  const isCompetition = type === 'competition'

  const openMenu = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation()
    hapticImpact('light')
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({
        top: rect.bottom + 4,
        left: Math.min(rect.right - 160, window.innerWidth - 170),
      })
    }
    setMenuOpen(true)
  }, [])

  const closeMenu = useCallback((): void => setMenuOpen(false), [])

  const handleShare = useCallback((): void => {
    closeMenu()
    const link = shortCode ? getChallengeShareLink(shortCode) : `${window.location.origin}/challenges/${challengeId}`
    const text = encodeURIComponent(`${name}\n${link}`)
    const shareUrl = `https://t.me/share/url?url=&text=${text}`
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      void navigator.share({ url: link, text: name }).catch(() => {
        window.open(shareUrl, '_blank')
      })
    } else {
      window.open(shareUrl, '_blank')
    }
  }, [shortCode, challengeId, name, closeMenu])

  const handleCopyLink = useCallback((): void => {
    closeMenu()
    const link = shortCode ? getChallengeShareLink(shortCode) : `${window.location.origin}/challenges/${challengeId}`
    void navigator.clipboard.writeText(link)
    hapticImpact('light')
  }, [shortCode, challengeId, closeMenu])

  const handleLeave = useCallback((): void => {
    closeMenu()
    onLeave?.(challengeId)
  }, [challengeId, closeMenu, onLeave])

  // Status color
  const statusColor = isCompleted ? 'text-success' : isUpcoming ? 'text-warning' : isActive ? 'text-primary' : 'text-text-hint'
  const statusText = isUpcoming && daysUntilStart != null
    ? `${daysUntilStart} ${t('challenges.days')} — ${t('challenges.status.upcoming').toLowerCase()}`
    : isActive && daysLeft != null && daysLeft > 0
      ? `${daysLeft} ${t('challenges.days')} ${t('challenges.profile.daysLeft', { count: daysLeft }).split(' ').slice(-1)[0]}`
      : t(`challenges.status.${status}`)

  return (
    <>
      <div
        onClick={() => navigate(`/challenges/${challengeId}`)}
        className="flex gap-3 p-2.5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-colors tap-highlight-transparent cursor-pointer"
      >
        {/* Cover — square with rounded corners */}
        <div
          className="relative shrink-0 w-[88px] h-[88px] rounded-xl overflow-hidden flex items-center justify-center"
          style={bgStyle}
        >
          {!coverImageUrl && coverEmoji && (
            <span className="text-3xl drop-shadow-sm">{coverEmoji}</span>
          )}
          {/* Attempt badge */}
          {attemptNumber != null && attemptNumber > 1 && (
            <div className="absolute top-1 left-1 bg-primary/90 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">
              x{attemptNumber}
            </div>
          )}
          {/* Type icon */}
          <div className="absolute bottom-1 right-1">
            {isPrivate && <Lock size={10} className="text-white/70" />}
            {isCompetition && <Trophy size={10} className="text-warning" />}
          </div>
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          {/* Top row: name + 3-dot */}
          <div className="flex items-start gap-1">
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-text leading-tight line-clamp-1">{name}</p>
              {creatorName && (
                <p className="text-[11px] text-text-hint/60 mt-0.5 truncate">{creatorName}</p>
              )}
            </div>
            <button
              ref={btnRef}
              onClick={openMenu}
              className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-text-hint/40 hover:text-text-hint hover:bg-white/[0.06] transition-colors tap-highlight-transparent"
            >
              <MoreVertical size={14} />
            </button>
          </div>

          {/* Meta row */}
          <div className="flex items-center gap-2.5 mt-1.5 text-[11px] text-text-hint">
            <span className="inline-flex items-center gap-1">
              <Calendar size={10} className="opacity-60" />
              {durationDays} {t('challenges.days')}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={10} className="opacity-60" />
              {membersCount.toLocaleString()}
            </span>
            {timeModel === 'evergreen' && (
              <span className="inline-flex items-center gap-1">
                <Clock size={10} className="opacity-60" />
                {t('challenges.browse.evergreen')}
              </span>
            )}
          </div>

          {/* Bottom row: status + progress or join */}
          <div className="flex items-center justify-between mt-1.5">
            <span className={`text-[11px] font-medium ${statusColor}`}>
              {statusText}
            </span>

            {/* Progress bar for active members */}
            {memberStatus === 'active' && completionRate != null && completionRate > 0 && (
              <div className="flex items-center gap-1.5 flex-1 ml-3">
                <div className="flex-1 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(completionRate, 100)}%` }} />
                </div>
                <span className="text-[10px] text-text-hint tabular-nums">{Math.round(completionRate)}%</span>
              </div>
            )}

            {/* Completed badge */}
            {memberStatus === 'completed' && completionRate != null && (
              <span className="text-[10px] font-semibold text-success">{Math.round(completionRate)}%</span>
            )}

            {/* Join button (for browse) */}
            {showJoinBtn && !memberStatus && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/challenges/${challengeId}`) }}
                className="shrink-0 flex items-center gap-0.5 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-[11px] font-semibold hover:bg-primary/20 transition-colors tap-highlight-transparent"
              >
                {t('challenges.browse.joinBtn')}
                <ChevronRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Portal menu */}
      {menuOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={closeMenu} />
          <div
            className="fixed z-[9999] bg-surface border border-white/[0.08] rounded-xl overflow-hidden min-w-[160px] shadow-xl"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <MenuBtn icon={ExternalLink} label={t('challenges.menu.open')} onClick={() => { closeMenu(); navigate(`/challenges/${challengeId}`) }} />
            <div className="h-px bg-white/[0.06] mx-2" />
            <MenuBtn icon={Share2} label={t('challenges.menu.share')} onClick={handleShare} />
            <MenuBtn icon={Copy} label={t('challenges.menu.copyLink')} onClick={handleCopyLink} />
            {!isCreator && memberStatus === 'active' && (
              <>
                <div className="h-px bg-white/[0.06] mx-2" />
                <MenuBtn icon={LogOut} label={t('challenges.menu.leave')} onClick={handleLeave} danger />
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  )
})

function MenuBtn({ icon: Icon, label, onClick, danger }: {
  icon: React.ElementType; label: string; onClick: () => void; danger?: boolean
}): React.ReactElement {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-[13px] hover:bg-white/[0.04] transition-colors ${danger ? 'text-danger' : 'text-text'}`}>
      <Icon size={14} className={danger ? '' : 'text-text-hint/60'} />
      {label}
    </button>
  )
}
